import { useState } from "react";
import { Calculator, DollarSign, Clock, Percent } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";

const PROJECT_TYPES = {
  "bathroom-remodeling": {
    name: "Bathroom Remodel",
    minPrice: 11000,
    maxPrice: 30000,
    defaultPrice: 18000,
  },
  "tub-shower-conversion": {
    name: "Tub & Shower Conversion",
    minPrice: 11000,
    maxPrice: 30000,
    defaultPrice: 15000,
  },
  "walk-in-tubs": {
    name: "Walk-In Tub",
    minPrice: 11000,
    maxPrice: 30000,
    defaultPrice: 18000,
  },
};

const FINANCING_TERMS = [
  { months: 12, rate: 0, paymentFactor: null },
  { months: 144, rate: 11.99, paymentFactor: 1.34 },
];

export const PaymentEstimator = ({ defaultProjectType = "bathroom-remodeling" }) => {
  const [projectType, setProjectType] = useState(defaultProjectType);
  const [projectCost, setProjectCost] = useState(PROJECT_TYPES[defaultProjectType]?.defaultPrice || 18000);
  const [selectedTerm, setSelectedTerm] = useState(144);
  const [showResults, setShowResults] = useState(false);

  const currentProject = PROJECT_TYPES[projectType];
  const selectedFinancing = FINANCING_TERMS.find((t) => t.months === selectedTerm);

  // Calculate monthly payment
  const calculateMonthlyPayment = () => {
    const principal = projectCost;
    
    // For 144 months with payment factor
    if (selectedFinancing.paymentFactor) {
      return (principal * selectedFinancing.paymentFactor) / 100;
    }
    
    // For 0% APR
    if (selectedFinancing.rate === 0) {
      return principal / selectedFinancing.months;
    }

    const monthlyRate = selectedFinancing.rate / 100 / 12;
    const months = selectedTerm;
    const payment =
      (principal * monthlyRate * Math.pow(1 + monthlyRate, months)) /
      (Math.pow(1 + monthlyRate, months) - 1);
    return payment;
  };

  const monthlyPayment = calculateMonthlyPayment();
  const totalCost = monthlyPayment * selectedTerm;
  const totalInterest = totalCost - projectCost;

  const handleCalculate = () => {
    setShowResults(true);
  };

  const scrollToForm = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <section className="py-20 bg-white" data-testid="payment-estimator-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="text-[#1E4C9A] font-semibold text-sm uppercase tracking-wider">
            Financing Options
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-3 mb-4 font-['Playfair_Display']" data-testid="estimator-title">
            Estimate Your Monthly Payment
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto mb-2">
            See how affordable your bathroom transformation can be with our flexible financing options.
          </p>
          <p className="text-[#1E4C9A] font-semibold">
            No pre-payment penalty fees on any financing options
          </p>
          <div className="section-divider mx-auto mt-4" />
        </div>

        {/* Estimator Card */}
        <div className="max-w-3xl mx-auto">
          <div className="bg-[#F9FAFB] rounded-2xl p-8 shadow-lg border border-gray-100">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-[#1E4C9A] rounded-xl flex items-center justify-center">
                <Calculator className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 font-['Playfair_Display']">
                Payment Calculator
              </h3>
            </div>

            <div className="space-y-8">
              {/* Project Type */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <span className="w-6 h-6 bg-[#1E4C9A] text-white rounded-full flex items-center justify-center text-xs">1</span>
                  Project Type
                </Label>
                <Select value={projectType} onValueChange={(value) => {
                  setProjectType(value);
                  setProjectCost(PROJECT_TYPES[value].defaultPrice);
                  setShowResults(false);
                }}>
                  <SelectTrigger className="h-12 bg-white" data-testid="project-type-select">
                    <SelectValue placeholder="Select project type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bathroom-remodeling">Bathroom Remodel</SelectItem>
                    <SelectItem value="tub-shower-conversion">Tub & Shower Conversion</SelectItem>
                    <SelectItem value="walk-in-tubs">Walk-In Tub</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Project Cost Slider */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <span className="w-6 h-6 bg-[#1E4C9A] text-white rounded-full flex items-center justify-center text-xs">2</span>
                    Estimated Project Cost
                  </Label>
                  <span className="text-2xl font-bold text-[#1E4C9A]" data-testid="project-cost-display">
                    ${projectCost.toLocaleString()}
                  </span>
                </div>
                <Slider
                  value={[projectCost]}
                  onValueChange={(value) => {
                    setProjectCost(value[0]);
                    setShowResults(false);
                  }}
                  min={currentProject?.minPrice || 11000}
                  max={currentProject?.maxPrice || 30000}
                  step={500}
                  className="py-4"
                  data-testid="project-cost-slider"
                />
                <div className="flex justify-between text-sm text-gray-500">
                  <span>${currentProject?.minPrice?.toLocaleString()}</span>
                  <span>${currentProject?.maxPrice?.toLocaleString()}</span>
                </div>
              </div>

              {/* Financing Term - 2 Options Only */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <span className="w-6 h-6 bg-[#1E4C9A] text-white rounded-full flex items-center justify-center text-xs">3</span>
                  Financing Term
                </Label>
                <div className="grid grid-cols-2 gap-4" data-testid="financing-terms">
                  {FINANCING_TERMS.map((term) => (
                    <button
                      key={term.months}
                      onClick={() => {
                        setSelectedTerm(term.months);
                        setShowResults(false);
                      }}
                      className={`p-4 rounded-xl border-2 text-center transition-all duration-200 ${
                        selectedTerm === term.months
                          ? "border-[#1E4C9A] bg-[#1E4C9A] text-white"
                          : "border-gray-200 bg-white hover:border-[#1E4C9A] text-gray-700"
                      }`}
                      data-testid={`term-${term.months}`}
                    >
                      <div className="font-bold text-lg">{term.months} Months</div>
                      <div className={`text-sm ${selectedTerm === term.months ? "text-white/80" : "text-gray-500"}`}>
                        {term.rate === 0 ? "0% APR" : `${term.rate}% APR`}
                      </div>
                      {term.paymentFactor && (
                        <div className={`text-xs mt-1 ${selectedTerm === term.months ? "text-white/70" : "text-gray-400"}`}>
                          ({term.paymentFactor}% payment factor)
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Calculate Button */}
              <Button
                onClick={handleCalculate}
                className="w-full h-14 bg-[#1E4C9A] hover:bg-[#153975] text-white font-semibold text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                data-testid="calculate-payment-btn"
              >
                <Calculator className="mr-2 h-5 w-5" />
                Calculate My Payment
              </Button>

              {/* Results */}
              {showResults && (
                <div className="mt-8 p-6 bg-white rounded-xl border-2 border-[#1E4C9A] animate-fade-in" data-testid="payment-results">
                  <div className="text-center mb-6">
                    <p className="text-gray-600 text-sm mb-2">Your Estimated Monthly Payment</p>
                    <div className="text-5xl font-bold text-[#1E4C9A] font-['Playfair_Display']" data-testid="monthly-payment">
                      ${Math.round(monthlyPayment).toLocaleString()}
                      <span className="text-lg text-gray-500 font-normal">/mo</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <DollarSign className="h-5 w-5 text-[#1E4C9A] mx-auto mb-1" />
                      <p className="text-xs text-gray-500">Project Cost</p>
                      <p className="font-bold text-gray-900">${projectCost.toLocaleString()}</p>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <Clock className="h-5 w-5 text-[#1E4C9A] mx-auto mb-1" />
                      <p className="text-xs text-gray-500">Term</p>
                      <p className="font-bold text-gray-900">{selectedTerm} months</p>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <Percent className="h-5 w-5 text-[#1E4C9A] mx-auto mb-1" />
                      <p className="text-xs text-gray-500">APR</p>
                      <p className="font-bold text-gray-900">{selectedFinancing.rate}%</p>
                    </div>
                  </div>

                  {selectedFinancing.rate === 0 && (
                    <div className="mb-6 p-3 bg-green-50 border border-green-200 rounded-lg text-center">
                      <p className="text-green-700 font-semibold text-sm">
                        0% Interest for 12 Months! No pre-payment penalties.
                      </p>
                    </div>
                  )}

                  <div className="text-center">
                    <Button
                      onClick={scrollToForm}
                      className="h-12 px-8 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                      data-testid="get-quote-from-estimator"
                    >
                      Get My Exact Quote
                    </Button>
                    <p className="text-xs text-gray-500 mt-3">
                      *Rates subject to credit approval. This is an estimate only.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
